/**
 * The Ash shop — the between-runs spend screen (issue #10).
 *
 * `purchasables()`, `tryPurchase()` and `ashBalance()` were written, exported,
 * documented, and had **zero callers**. Ash accrued at 3/12/40 per kill plus the
 * end-of-run bank and could never be spent, so all twelve Ash unlocks were
 * permanently unbuyable. This file is the layer that gives them a caller; the
 * run-setup screen is the surface.
 *
 * **Ash is meta and stays meta.** Scrip (#29) is the in-run currency: earned in
 * the run, spent in the run, lost on death. The two must read as visibly
 * different or players will conflate them, so this file exports both as
 * `CurrencyVM` descriptors rather than letting the panel invent labels: one says
 * "permanent", one says "this run", and the sentence under each says what
 * happens to it when you die. Letting a between-runs screen quote a price in the
 * same word a vendor uses is how a player ends up spending a permanent unlock on
 * a weapon they will lose in ten minutes.
 *
 * Nothing here mutates progression except `buyFromAshShop`, which delegates to
 * `tryPurchase` → `Progression.purchase`. The debit and the grant remain one
 * operation in one place.
 */

import {
  ashBalance,
  purchasables,
  tryPurchase,
} from './unlockEffects';
import { UNLOCK_BY_ID } from './progression';
import type { Progression, UnlockGrant } from './progression';

// ------------------------------------------------------------- the currencies

export interface CurrencyVM {
  id: 'ash' | 'scrip';
  name: string;
  /** Two words the panel can print beside the number. Never the same for both. */
  scope: 'permanent' | 'this run';
  /** What happens to it when you die. The whole distinction, in one sentence. */
  blurb: string;
  /** A glyph so the two are distinguishable at a glance, not only by reading. */
  glyph: string;
}

export const ASH_CURRENCY: CurrencyVM = Object.freeze({
  id: 'ash',
  name: 'Ash',
  scope: 'permanent',
  blurb: 'Banked when a run ends, win or lose. Spent here, between runs.',
  glyph: '◆',
});

export const SCRIP_CURRENCY: CurrencyVM = Object.freeze({
  id: 'scrip',
  name: 'Scrip',
  scope: 'this run',
  blurb: 'Earned and spent inside one run. Lost the moment you die.',
  glyph: '○',
});

// ------------------------------------------------------------ grant summaries

/**
 * What an unlock actually hands over, in one phrase.
 *
 * Issue #10 asks for "what each grant does", and `UnlockDef.desc` is written as
 * flavour ("Start loud."), which is the right copy for a kit list and the wrong
 * copy for a price tag. This restates the *grant* mechanically, so a shop row
 * reads "250 Ash · a starting kit" and the flavour sits underneath.
 *
 * Deliberately **not** exhaustive over `UnlockGrant`, unlike
 * `core/unlockApply.ts`. That file is the one place that must name all eleven
 * kinds, and its `never` arm is what makes a twelfth a compile error; a second
 * exhaustive switch would fail to compile in two places for the same reason and
 * tempt someone to satisfy this one with a stub. A new kind falling through to
 * the default here costs a shop label, which the fallback still gets right.
 */
export function describeGrant(g: UnlockGrant): string {
  switch (g.kind) {
    case 'archetype':
      return `the ${g.archetype} body at run start`;
    case 'attunement':
      return `${g.ability} as a run-start option`;
    case 'attunement-slot':
      return g.slots === 1 ? 'one more attunement slot' : `${g.slots} more attunement slots`;
    case 'kit':
      return 'a starting kit';
    case 'weapons':
      return `${g.classes.length} weapon classes in the drop pool`;
    case 'modules':
      return `${g.families.length} module families in the drop pool`;
    case 'recipes':
      return `${g.recipes.length} crafting recipes at rest nodes`;
    case 'world-modifier':
      return 'an opt-in world modifier on the setup screen';
    case 'stash':
      return `${g.slots} stash slots that survive death`;
    case 'rest-node':
      return 'a service at rest nodes';
    case 'passive':
      return 'a permanent passive';
    default:
      return 'a permanent unlock';
  }
}

// -------------------------------------------------------------------- the shop

export interface ShopEntryVM {
  id: string;
  name: string;
  cost: number;
  /** Payable from the current balance right now. Never doubles as "allowed". */
  affordable: boolean;
  /** The unlock's own flavour copy. */
  description: string;
  /** What it mechanically grants — `describeGrant`. */
  grant: string;
}

export interface AshShopVM {
  currency: CurrencyVM;
  /** The other currency, so the panel can print the distinction without inventing it. */
  contrast: CurrencyVM;
  balance: number;
  entries: ShopEntryVM[];
  /**
   * Non-empty when `entries` is empty, saying *why*. An empty shop with no
   * explanation is indistinguishable from a broken one — which is the exact
   * failure mode this issue is about, one layer up.
   */
  empty: string | null;
  /** Set after a purchase attempt, for the panel to print. Cleared on rebuild. */
  message: string | null;
}

/**
 * Everything the spend screen renders.
 *
 * The list is `purchasables()` verbatim, which means prereq-blocked entries are
 * *absent* rather than shown as unaffordable — see that function's own note. A
 * shop that wants to preview the whole ramp reads `Progression.unlockables()`;
 * this one only ever shows things a player can act on, so `affordable` means
 * exactly one thing.
 */
export function buildAshShopVM(prog: Progression, message: string | null = null): AshShopVM {
  const balance = ashBalance(prog);
  const entries = purchasables(prog).map((p): ShopEntryVM => {
    const def = UNLOCK_BY_ID[p.id];
    return {
      id: p.id,
      name: p.label,
      cost: p.cost,
      affordable: p.affordable,
      description: p.description,
      grant: def ? describeGrant(def.grants) : 'a permanent unlock',
    };
  });
  return {
    currency: ASH_CURRENCY,
    contrast: SCRIP_CURRENCY,
    balance,
    entries,
    empty: entries.length
      ? null
      : balance > 0
        ? 'Everything Ash can buy is bought. The other four tracks are earned, not spent.'
        : 'Nothing available yet — finish a run to bank Ash.',
    message,
  };
}

export interface ShopPurchaseResult {
  ok: boolean;
  /** Always set, success or failure — the panel prints this either way. */
  message: string;
  /** The rebuilt view model, with the balance and list already updated. */
  vm: AshShopVM;
}

/**
 * Buy, and hand back a screen that already reflects it.
 *
 * Returning the rebuilt view model rather than a bare boolean is what makes the
 * integrator's job one line: the balance, the remaining list and the message are
 * all consequences of the same purchase and there is no ordering in which a
 * caller can show a stale one.
 *
 * Idempotent by construction — `tryPurchase` refuses an already-owned id with a
 * reason, so a double-clicked button debits once and says so the second time.
 */
export function buyFromAshShop(prog: Progression, id: string): ShopPurchaseResult {
  const res = tryPurchase(prog, id);
  const name = UNLOCK_BY_ID[id]?.name ?? id;
  const message = res.ok
    ? `Bought ${name} — ${ashBalance(prog)} Ash left.`
    : (res.reason ?? `could not buy ${name}`);
  return { ok: res.ok, message, vm: buildAshShopVM(prog, message) };
}
